import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { useSession } from '../contexts/session-context';
import {
    getUserSettingsStatus,
    loadUserSettings,
    readUserSetting,
    resetUserSetting,
    setUserSetting,
    subscribeUserSettings,
    type UserSettingsStatus
} from '../services/user-settings';
import type { UserSettingDefinition } from '../types/user-settings';

export interface UseUserSettingResult<T> {
    /**
     * The current value, always usable: defaults when nothing is stored or what is stored is
     * malformed. Keeps its identity until the value really changes.
     */
    value: T;
    /** Applies at once in this tab and is sent to the server, debounced, as a merge patch. */
    setValue: (value: T) => void;
    /** Removes the stored value, so the setting reads as its defaults again. */
    reset: () => void;
    /**
     * Where the server read stands. `value` is usable in every status: before `loaded` it comes
     * from the localStorage mirror of this user's last known document.
     */
    status: UserSettingsStatus;
}

/**
 * One per-user UX setting from the server-side settings document (API-617). See
 * `services/user-settings` for how the store behaves.
 *
 * The user is the session's effective user, so under Login As it is the impersonated user's
 * settings that load. Works in any shell: all three mount `SessionProvider`, and the store needs
 * no provider of its own. The document is fetched the first time any setting is used.
 *
 * `definition` must be a module-level constant.
 */
export function useUserSetting<T>(definition: UserSettingDefinition<T>): UseUserSettingResult<T> {
    const { user } = useSession();
    const userId = user?.userId;

    const value = useSyncExternalStore(subscribeUserSettings, () =>
        readUserSetting(userId, definition)
    );
    const status = useSyncExternalStore(subscribeUserSettings, () => getUserSettingsStatus(userId));

    useEffect(() => {
        void loadUserSettings(userId);
    }, [userId]);

    const setValue = useCallback(
        (next: T) => setUserSetting(userId, definition, next),
        [userId, definition]
    );
    const reset = useCallback(() => resetUserSetting(userId, definition), [userId, definition]);

    return { value, setValue, reset, status };
}
